import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { COMPONENT_TYPES, ELEMENT_TYPES, KIDSMODE } from '../../config/constants';
import h from '../../helpers/common';
import f from '../../helpers/formatter';
import user from '../../helpers/user';
import { __ } from '../../translations';
import { getUserInfo } from '../../utils/user';
import WatchListEpisodeItem from '../elements/WatchListEpisodeItem';
import WatchListRewindItem from '../elements/WatchListRewindItem';
import WatchListVodItem from '../elements/WatchListVodItem';
import { Box, FlexWrapper, FontIcon, Separator, Skeleton, Spinner, Text } from '@intigral/shared';
import { Carousel } from '@intigral/domain/lib/core/entities/ScreenEntities';
import { IStore } from '../../interfaces/StoreTypes';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import withPresetWidth from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import { Carousel2, MovieCard } from '@intigral/ui';
import { useUserWatchList } from '../../hooks/watchlist/useUserWatchlist';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { STORE_NAME } from '@intigral/constant';

const BoxWidth = withPresetWidth(Box);

const LANDSCAPE_RAIL = {
    slidesPerView: 1,
    breakpoints: {
        560: {
            slidesPerView: 2,
        },
        824: {
            slidesPerView: 3,
        },
        1212: {
            slidesPerView: 4,
        },
        1500: {
            slidesPerView: 5,
        },
    },
};

interface Languages {
    ar?: string;
    en?: string;
}
interface UserWatchlistProps {
    name: string;
    meta: {
        title?: Languages;
    };
    carousel: Carousel;
    data: any;
}

export const UserWatchlist: FC<UserWatchlistProps> = (props) => {
    const location = useSelector((store: IStore) => store.location);
    // Using React Query instead of Redux for configuration
    const { promotionProviderImages } = useConfigurationSections();
    const providers = promotionProviderImages;
    const selectedProfile = get(STORE_NAME.SELECTED_PROFILE);

    const { data: detailedWatchlist, isLoading } = useUserWatchList(
        selectedProfile?.profileGuid,
        getUserInfo(),
        location.countryCode,
        providers,
        h.getLang()
    );
    const remindersList =
        user.getProfileMode() !== KIDSMODE.KID
            ? Object.values(useSelector((state: IStore) => state.reminder.remindersData))
            : [];

    // In case there is wishlist and not reminders
    if (detailedWatchlist?.length <= 0 && remindersList?.length > 0) return;

    if (isLoading)
        return (
            <div className="container full-width">
                <Skeleton variant="text" height={36} />
                <Separator theme={'none'} />
                <Carousel2 isLoading>
                    <MovieCard isLoading />
                </Carousel2>
            </div>
        );

    return (
        <React.Fragment>
            <div className="container full-width watchlist" data-id="movies-section">
                <Text
                    as="h2"
                    variant="h2"
                    alignment="start"
                    margin={{ default: '2x', md: '3x' }}
                    size={{ default: '18', md: '28' }}
                >
                    {f.getComponentTitle(props.meta)}
                </Text>

                {detailedWatchlist && detailedWatchlist.length ? (
                    <>
                        {detailedWatchlist.some((item) => item.category === 'LINEAR') && (
                            <div className="watchlist-group" data-group="linear">
                                <Carousel2 swiperSettings={LANDSCAPE_RAIL}>
                                    {detailedWatchlist
                                        .filter((item) => item.category === 'LINEAR')
                                        .map((item, index) => (
                                            <WatchListRewindItem
                                                key={item.Id}
                                                item={item}
                                                onRemove={() => undefined}
                                                componentType={COMPONENT_TYPES.USER_WATCHLIST}
                                                componentName={props.name}
                                                id={ELEMENT_TYPES.WATCHLIST_LINEAR_ITEM + '_' + index}
                                            />
                                        ))}
                                </Carousel2>
                            </div>
                        )}
                        {detailedWatchlist.some(
                            (item) => item.category === 'VOD' && item.programType === 'episode'
                        ) && (
                            <div className="watchlist-group" data-group="episodes">
                                <Carousel2 swiperSettings={LANDSCAPE_RAIL}>
                                    {detailedWatchlist
                                        .filter((item) => item.category === 'VOD' && item.programType === 'episode')
                                        .map((item, index) => (
                                            <WatchListEpisodeItem
                                                key={item.Id}
                                                item={item}
                                                onRemove={() => undefined}
                                                componentType={COMPONENT_TYPES.USER_WATCHLIST}
                                                componentName={props.name}
                                                id={ELEMENT_TYPES.WATCHLIST_EPISODE_ITEM + '_' + index}
                                            />
                                        ))}
                                </Carousel2>
                            </div>
                        )}
                        {detailedWatchlist.some(
                            (item) => item.category === 'VOD' && item.programType !== 'episode'
                        ) && (
                            <div className="watchlist-group" data-group="vod">
                                <Carousel2>
                                    {detailedWatchlist
                                        .filter((item) => item.category === 'VOD' && item.programType !== 'episode')
                                        .map((item, index) => (
                                            <WatchListVodItem
                                                key={item.Id}
                                                item={item}
                                                onRemove={() => undefined}
                                                componentType={COMPONENT_TYPES.USER_WATCHLIST}
                                                componentName={props.name}
                                                id={ELEMENT_TYPES.WATCHLIST_VOD_ITEM + '_' + index}
                                                meta={props.meta}
                                            />
                                        ))}
                                </Carousel2>
                            </div>
                        )}
                    </>
                ) : (
                    <BoxWidth theme="dark" presetWidth="medium" padding="4x" marginBottom="6x">
                        <FlexWrapper flexDirection="row" alignItems="center" gap="3x">
                            <FontIcon name="plus-circle" color="green" size="size60" md="size60" />
                            <Text as="p" size="20" color="white" margin="0" alignment="start">
                                {__('watchlist_page_empty_message')}
                            </Text>
                        </FlexWrapper>
                    </BoxWidth>
                )}
            </div>
        </React.Fragment>
    );
};
